import React, { useState } from 'react'; 
import Button from '@/components/Buttons/Button';
import Input from "@/components/form/Input/Input.tsx";
import TextArea from "@/components/form/TextArea"; 
import CustomSelect from '@/components/form/Select';
import ListPlayers from './ListPlayers'; 
import { IUserSelect } from '@/integration/user/interfaces/IUser.ts';

import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { RegistrationTeamSchema } from '@/integration/team/interfaces/validations/teams.schema';
import { ICreateTeamRequest, TeamMembersRoles, IInviteMemberRequest } from '@/integration/team/interfaces/team.interface.ts';
import ToastContextExports from '@/context/ToastContext';
import ImageUpload from '@/components/ImageUpload';

import {FileEntityType, FileBussinessType, FileImageCropper } from '@/integration/application/interfaces/file.interface.ts';
import {X} from 'lucide-react';
import Avatar from '@/components/others/Avatar';
import useFile from "@integration/application/hooks/useFile.ts";
import useTeam from "@integration/team/hooks/useTeam.ts";
import useGamePublic from "@integration/game/hooks/useGamePublic.ts";

const SelectedPlayersList: React.FC<{ players: IUserSelect[], onRemove: (player: IUserSelect) => void }> = ({ players, onRemove }) => {
  if (players.length === 0) return null;

  return (
    <div className="mb-6">
      <h3 className="text-sm font-medium text-white mb-3">Jogadores Selecionados:</h3>
      <div className="flex flex-wrap gap-2">
        {players.map((player) => (
          <div 
            key={player.id} 
            className="flex items-center gap-2 bg-gray-800/80 border border-gray-700 hover:border-primary-600/50 rounded-full px-3 py-1.5 shadow-sm transition-all duration-200"
          >
            <Avatar 
              src={player?.picture} 
              children={player?.name.charAt(0)} 
              sizeClass='w-7 h-7' 
            />
            <span className="text-sm font-medium text-white">{player.name}</span>
            <button
              onClick={() => onRemove(player)}
              className="text-gray-400 hover:text-red-400 transition-colors ml-1"
              aria-label="Remove player"
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const FormCreateTeam: React.FC<{ onClose: () => void }> = ({ onClose }) => {

  const { uploadFile } = useFile();
  const [banner, setBanner] = useState<string | null>(null);
  const [logo, setLogo] = useState<string | null>(null);
  const [step, setStep] = useState(1);
  const [shouldResetList, setShouldResetList] = useState(false);

  const [selectedPlayers, setSelectedPlayers] = useState<IUserSelect[]>([]); 
  const { createTeam, sendInvitations } = useTeam();

  const { showToast } = ToastContextExports.useToast();

  const handleNextStep = async () => {
    if (step === 1) {
      const isValid = await trigger(['name', 'teamTag', 'game_id', 'description']);

      if (isValid) {
        setStep(2);
      } else {
        showToast("Verifique os campos", "Por favor, preencha todos os campos corretamente antes de continuar.", "error");
      }
    } else if (step === 2) {
      setStep(3);
    }
  };

  const handlePreviousStep = () => {
    if (step === 3) {
      setShouldResetList(true);
      // Reseta o shouldResetList após um pequeno delay
      setTimeout(() => setShouldResetList(false), 100);
    }
    setStep(step - 1);
  };

  const handleCancel = () => {
    onClose();
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();

    if (step === 3) {
        handleSubmit(createTeamAndInvite)(e);
    } else {
      handleNextStep();
    }
  };

  const entityUploadFileRequest = (entityId: string, businessType: FileBussinessType): FileImageCropper => {
    return {
      entity_id: entityId,
      url:'',
      entity_type: FileEntityType.TEAM,
      business_type: businessType,

    } 
  }


  const handleUpload = (  base64: string, preset: string) => {

      if(preset === 'banner'){
        setBanner(base64);
      }else{
        setLogo(base64);
      }
  };     

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    trigger,
  } = useForm({
    resolver: yupResolver(RegistrationTeamSchema),
  });


const createTeamAndInvite: SubmitHandler<ICreateTeamRequest> = async (
    values,
  ) => {
    // Previne o submit se não estiver no último step
    if (step !== 3) {
      return;
    }

    const result = await createTeam(values);

    if (result) {
      if (selectedPlayers.length > 0) {
          // @ts-ignore
          await sendInvitations(result.id, transformSelectedPlayers(selectedPlayers));
      }
    }

    if (logo) {
        // @ts-ignore
      const entityLogo = entityUploadFileRequest(result.id!, FileBussinessType.TEAM_PROFILE_PHOTO);
      await uploadFile(logo, entityLogo);
    }
    if (banner) {
        // @ts-ignore
      const entityBanner = entityUploadFileRequest(result.id!, FileBussinessType.TEAM_BANNER_PHOTO);
      await uploadFile(banner, entityBanner);
    }

    return result;
  };



const transformSelectedPlayers = (selectedPlayers: IUserSelect[]): IInviteMemberRequest[] => {
  return selectedPlayers.map((player): IInviteMemberRequest => ({
    userId: player.id,
    role: TeamMembersRoles.PLAYER
  }));
};



  const { gamesFilter } = useGamePublic();

  const handleChange = (value: string) => {

    setValue("game_id", value);
  };

  const handlePlayerSelect = (players: IUserSelect[]) => {
    setSelectedPlayers(players); 
  };

  const handleRemovePlayer = (playerToRemove: IUserSelect) => {
    setSelectedPlayers(selectedPlayers.filter(player => player.id !== playerToRemove.id));
  };

  // Step titles and descriptions for better UX
  const stepInfo = [
    {
      title: "Informações do Time",
      description: "Preencha os dados básicos do seu time"
    },
    {
      title: "Imagens do Time",
      description: "Faça upload do logo e banner do seu time"
    },
    {
      title: "Convide os jogadores para o time",
      description: "Selecione os jogadores que você deseja convidar para fazer parte do seu time. Eles receberão um convite e poderão aceitar ou recusar."
    }
  ];

  return (
    <div className="w-full h-full p-0  rounded-lg">
      {/* Step indicators */}
      <div className="flex justify-between items-center px-4 py-3 border-b border-gray-700 mb-4">
        {[1, 2, 3].map((stepNumber) => (
          <div key={stepNumber} className="flex flex-col items-center">
            <div 
              className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 transition-all
                ${step === stepNumber 
                  ? 'bg-primary-600 text-white' 
                  : step > stepNumber 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-700 text-gray-300'}`}
            >
              {step > stepNumber ? '✓' : stepNumber}
            </div>
            <span className={`text-xs font-medium ${step === stepNumber ? 'text-primary-600' : 'text-gray-400'} text-center w-full block max-w-[120px]`}>
              {stepInfo[stepNumber-1].title}
            </span>
          </div>
        ))}
      </div>

      <div className="px-4 pb-2">
        <h2 className="text-xl font-bold text-white mb-1">{stepInfo[step-1].title}</h2>
        <p className="text-sm text-gray-400 mb-4">{stepInfo[step-1].description}</p>
      </div>

      <form 
        onSubmit={handleSubmitForm}
        className="space-y-6 px-4 pb-6"
      >
        {step === 1 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Input  
                  label="Nome do time"
                  {...register("name")}
                  type="text" 
                  placeholder="Digite o nome do time"
                  className={`bg-gray-800/70 border border-gray-700 focus:border-primary-500 rounded-md shadow-sm text-white transition-all duration-200 ${errors.name ? 'border-red-400 focus:border-red-500' : ''}`}
                />
                {errors.name && (
                  <span className='text-red-400 text-xs flex items-center gap-1'>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.name?.message}
                  </span>
                )}
              </div>

              <div className="space-y-2">

                <Input  
                  label="TAG do Time"
                  {...register("teamTag")}
                  type="text" 
                  placeholder="Ex: PRO"
                  className={`bg-gray-800/70 border border-gray-700 focus:border-primary-600 rounded-md shadow-sm text-white uppercase transition-all duration-200 ${errors.teamTag ? 'border-red-400 focus:border-red-500' : ''}`}
                /> 
                {errors.teamTag && (
                  <span className='text-red-400 text-xs flex items-center gap-1'>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.teamTag?.message}
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="game" className="block text-sm font-semibold text-white">
                Jogo do Time
              </label>
              <CustomSelect
                {...register("game_id")}
                options={gamesFilter.map(game => ({ value: game.id, label: game.name }))}
                placeholder="Selecione o jogo principal do time"
                onValueChange={handleChange}
                borderColor="border border-gray-700"
                backgroundColor="bg-gray-800"
                className="w-full bg-gray-800/70 border border-gray-700 focus:border-primary-600 rounded-md shadow-sm text-white"
                error={!!errors.game_id}
              />
              {errors.game_id && (
                <span className='text-red-400 text-xs flex items-center gap-1'>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.game_id?.message}
                </span>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="description" className="block text-sm font-semibold text-white">
                Descrição do Time
              </label>
              <TextArea
                placeholder='Descreva o seu time, objetivos, conquistas, etc.'
                className={`bg-gray-800/70 border border-gray-700 focus:border-primary-600 rounded-md shadow-sm text-white min-h-[90px] max-h-3 transition-all duration-200 ${errors.description ? 'border-red-400 focus:border-red-500' : ''}`}
                {...register("description")} 
              />
              {errors.description && (
                <span className='text-red-400 text-xs flex items-center gap-1'>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.description?.message}
                </span>
              )}

            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button 
                className='text-gray-300 hover:text-white hover:bg-gray-700/50 transition-all duration-200 px-4 py-2 '
                hoverColor='hover:text-white'
                label="Cancelar" 
                outline={true}
                type="button"
                onClick={handleCancel}
              />
              <Button
                backgroundColor='bg-primary-600'
                hoverColor='hover:bg-primary-700'
                className='transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 px-5 py-2  font-medium'
                label="Próximo"
                onClick={handleNextStep}
                type="button"
              />
            </div>
          </>
        ) : step === 2 ? (
          <div className="space-y-6">
            <div className="flex flex-col gap-10">
              <div className="bg-gray-800/50 rounded-lg p-4   border-gray-700 hover:border-blue-500/30 transition-all duration-300 shadow-md">
                <label className="block text-sm font-semibold text-white mb-3 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-primary-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                  </svg>
                  Logo do Time
                </label>
                <div className="bg-gray-900/50 rounded-md p-3   border-gray-700">
                  <ImageUpload
                    onImageUpload={handleUpload}
                    preset="profile"  
                    maxSizeMB={5}
                  />
                  <p className="text-xs text-gray-400 mt-2">Recomendado: Imagem quadrada (1:1), máximo 5MB</p>
                </div>
                {logo && (
                  <div className="mt-3 text-center">
                    <span className="text-xs text-green-400 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Logo carregado com sucesso
                    </span>
                  </div>
                )}
              </div>

              <div className="bg-gray-800/50 rounded-lg p-4   border-gray-700 hover:border-primary-600/30 transition-all duration-300 shadow-md">
                <label className="block text-sm font-semibold text-white mb-3 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-primary-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm0 2h12v4H4V5zm0 6h12v4H4v-4z" clipRule="evenodd" />
                  </svg>
                  Banner do Time
                </label>
                <div className="bg-gray-900/50 rounded-md p-3 border border-gray-700">
                  <ImageUpload
                    onImageUpload={handleUpload}
                    preset="banner" 
                    maxSizeMB={5}
                  />
                  <p className="text-xs text-gray-400 mt-2">Recomendado: Imagem retangular (16:9), máximo 5MB</p>
                </div>
                {banner && (
                  <div className="mt-3 text-center">
                    <span className="text-xs text-green-400 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Banner carregado com sucesso
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                className='text-gray-300 hover:text-white hover:bg-gray-700/50 transition-all duration-200 px-4 py-2 rounded-md'
                hoverColor='hover:text-white'
                label="Voltar"
                onClick={handlePreviousStep}
                type="button"
                outline
              />
              <Button
                backgroundColor='bg-primary-600'
                hoverColor='hover:bg-primary-700'
                className='transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 px-5 py-2 rounded-md font-medium'
                label="Próximo" 
                onClick={handleNextStep}
                type="button"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-6">
              
              <ListPlayers
                onPlayerSelect={handlePlayerSelect} 
                selectedPlayers={selectedPlayers}
                shouldReset={shouldResetList}
                multiSelect={true}
              />

              <SelectedPlayersList 
                players={selectedPlayers} 
                onRemove={handleRemovePlayer}
              />

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                className='text-gray-300 hover:text-white hover:bg-gray-700/50 transition-all duration-200 px-4 py-2 rounded-md'
                hoverColor='hover:text-white'
                label="Voltar"
                onClick={handlePreviousStep}
                type="button"
                outline
              />
              <Button 
                backgroundColor='bg-primary-600'
                hoverColor='hover:bg-primary-700'
                className='transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 px-5 py-2 rounded-md font-medium'
                label="Criar Time" 
                type="button"
                onClick={() => handleSubmitForm(new Event('submit') as any)}
              />
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default FormCreateTeam;
